import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertCircle,
  FileStack,
  FileText,
  ChevronLeft,
  ChevronRight,
  Search,
  Calendar,
  User,
  Loader2,
  Pencil,
} from "lucide-react";
import { useNeedsReviewDocuments, useDocumentPreview, DOC_TYPES_WITHOUT_ACTION, documentUsesLotId, type ScannedDocument } from "@/hooks/useDocuments";
import { PDFViewer } from "@/components/documents/PDFViewer";
import { format, formatDistanceToNow } from "date-fns";

interface NeedsReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDocumentReview: (documentId: string) => void;
}

const PAGE_SIZE = 10;

function getMissingFields(doc: ScannedDocument) {
  const missing: string[] = [];
  const pr = doc.parse_result;
  if (!pr) return ["No parse data"];
  if (!pr.parsed_date) missing.push("Date");
  if (!pr.parsed_client_id && !pr.parsed_client_name) missing.push("Client");
  if (!pr.parsed_lot_id && documentUsesLotId(pr.doc_type, pr.parsed_action)) missing.push("Lot ID");
  if (!pr.doc_type) missing.push("Doc Type");
  if (!pr.parsed_action && !(pr.doc_type && DOC_TYPES_WITHOUT_ACTION.has(pr.doc_type))) missing.push("Action");
  return missing;
}

function getConfidenceBadgeColor(score: number | null) {
  if (score === null || score === undefined) return "bg-slate-500";
  if (score >= 0.75) return "bg-green-500";
  if (score >= 0.5) return "bg-amber-500";
  return "bg-red-500";
}

function DetailPanel({
  document,
  onReview,
}: {
  document: ScannedDocument;
  onReview: () => void;
}) {
  const { data: previewUrl, isLoading: previewLoading } = useDocumentPreview(document.id);
  const missingFields = getMissingFields(document);
  const confidence = document.parse_result?.confidence_score;

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* PDF Preview */}
      <div className="flex-1 min-h-0 p-4">
        {previewLoading ? (
          <div className="flex items-center justify-center h-full border rounded-lg bg-muted">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : previewUrl ? (
          <PDFViewer url={previewUrl} className="h-full" />
        ) : (
          <div className="flex flex-col items-center justify-center h-full border rounded-lg bg-muted/50 text-muted-foreground">
            <FileText className="h-10 w-10 mb-2" />
            <p className="text-sm">Preview unavailable</p>
          </div>
        )}
      </div>

      {/* Metadata */}
      <div className="px-4 py-3 border-t shrink-0 space-y-2.5">
        {/* Confidence */}
        {confidence != null && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Confidence:</span>
            <Badge className={getConfidenceBadgeColor(confidence)}>
              {Math.round(confidence * 100)}%
            </Badge>
          </div>
        )}

        {/* Missing fields */}
        {missingFields.length > 0 && (
          <div className="flex items-start gap-2 text-sm">
            <AlertCircle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <span className="text-muted-foreground">Missing:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {missingFields.map((field) => (
                  <Badge key={field} variant="outline" className="text-xs">
                    {field}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Parsed metadata summary */}
        {document.parse_result && (
          <>
            <Separator />
            {document.parse_result.parsed_date && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">Date:</span>
                <span className="font-medium">{document.parse_result.parsed_date}</span>
              </div>
            )}
            {(document.parse_result.parsed_client_name || document.parse_result.parsed_client_code) && (
              <div className="flex items-center gap-2 text-sm">
                <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">Client:</span>
                <span className="font-medium truncate">
                  {document.parse_result.parsed_client_name || document.parse_result.parsed_client_code}
                </span>
              </div>
            )}
            {document.parse_result.parsed_lot_id && (
              <div className="flex items-center gap-2 text-sm">
                <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">Lot:</span>
                <span className="font-medium font-mono">{document.parse_result.parsed_lot_id}</span>
              </div>
            )}
            {document.parse_result.doc_type && (
              <div className="flex items-center gap-2 text-sm">
                <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">Type:</span>
                <Badge variant="outline" className="text-xs">{document.parse_result.doc_type}</Badge>
                {document.parse_result.parsed_action && (
                  <Badge variant="outline" className="text-xs">{document.parse_result.parsed_action}</Badge>
                )}
              </div>
            )}
          </>
        )}

        {/* Review & Edit button */}
        <Separator />
        <Button className="w-full" onClick={onReview}>
          <Pencil className="h-4 w-4 mr-2" />
          Review & Edit
        </Button>
      </div>
    </div>
  );
}

export function NeedsReviewDialog({ open, onOpenChange, onDocumentReview }: NeedsReviewDialogProps) {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const { data: documents, isLoading } = useNeedsReviewDocuments();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setPage(0);
      setSearch("");
      setSelectedDocId(null);
    }
    onOpenChange(open);
  };

  const query = search.toLowerCase();
  const filteredDocs = query && documents
    ? documents.filter((doc) =>
        doc.original_filename.toLowerCase().includes(query)
      )
    : documents || [];

  const totalPages = Math.ceil(filteredDocs.length / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(totalPages - 1, 0));
  const paginatedDocs = filteredDocs.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  const selectedDoc = documents?.find((d) => d.id === selectedDocId) || null;

  const handleReview = (documentId: string) => {
    onOpenChange(false);
    onDocumentReview(documentId);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-7xl w-[95vw] h-[90vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            Needs Review
            <Badge variant="outline" className="ml-auto text-sm font-normal">
              {documents?.length || 0} documents
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          {/* Left pane - List */}
          <div className="w-[45%] flex flex-col border-r min-h-0">
            {/* Search */}
            <div className="relative px-4 py-3 shrink-0">
              <Search className="absolute left-7 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by filename..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="pl-9 focus-visible:ring-1 focus-visible:ring-border focus-visible:ring-offset-0"
              />
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {isLoading ? (
                <div className="space-y-3 p-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-14 w-full" />
                  ))}
                </div>
              ) : !filteredDocs.length ? (
                <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                  <FileStack className="h-10 w-10 text-muted-foreground/50 mb-3" />
                  <p className="text-sm font-medium">
                    {search ? "No matching documents" : "No documents need review"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {search ? "Try a different search term" : "All documents have been processed"}
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {paginatedDocs.map((doc) => {
                    const isSelected = selectedDocId === doc.id;
                    const confidence = doc.parse_result?.confidence_score;
                    const missingFields = getMissingFields(doc);
                    return (
                      <div
                        key={doc.id}
                        className={`px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors border-l-2 ${isSelected ? "border-l-amber-500 bg-muted" : "border-l-transparent"}`}
                        onClick={() => setSelectedDocId(doc.id)}
                      >
                        <div className="flex items-center gap-2">
                          <FileStack className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-sm font-medium truncate">{doc.original_filename}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 ml-6 flex-wrap">
                          <span className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(doc.detected_at), { addSuffix: true })}
                          </span>
                          {confidence != null && (
                            <Badge className={`text-[10px] px-1.5 py-0 ${getConfidenceBadgeColor(confidence)}`}>
                              {Math.round(confidence * 100)}%
                            </Badge>
                          )}
                          {missingFields.length > 0 && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-amber-600 border-amber-300">
                              {missingFields.length} missing
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-2 border-t shrink-0">
                <p className="text-xs text-muted-foreground">
                  {safePage * PAGE_SIZE + 1}-{Math.min((safePage + 1) * PAGE_SIZE, filteredDocs.length)} of {filteredDocs.length}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={safePage === 0}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  <span className="text-xs px-1.5">
                    {safePage + 1} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={safePage >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Right pane - Detail */}
          <div className="w-[55%] min-h-0">
            {selectedDoc ? (
              <DetailPanel
                document={selectedDoc}
                onReview={() => handleReview(selectedDoc.id)}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <FileText className="h-12 w-12 mb-3 opacity-30" />
                <p className="text-sm">Select a document to preview</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
